import React from "react";

/**
 * SidebarShell - Reusable sidebar container with overlay
 *
 * Props:
 * - isOpen: boolean - Controls visibility
 * - onClose: () => void - Close handler
 * - title: string - Header title
 * - children: ReactNode - Content to render inside sidebar
 * - width?: string - Sidebar width (default: "400px")
 * - showOverlay?: boolean - Whether to show overlay (default: true)
 * - overlayOpacity?: number - Overlay opacity 0-1 (default: 0.3)
 * - contentPadding?: string - Content area padding (default: "0")
 * - className?: string - Additional CSS classes
 * - dataTestId?: string - Test ID for automation
 */
function SidebarShell({
  isOpen,
  onClose,
  title,
  children,
  width = "400px",
  showOverlay = true,
  overlayOpacity = 0.3,
  contentPadding = "0",
  className = "",
  dataTestId,
  zIndex = 1000,
  showCloseButton = true,
}) {
  if (!isOpen) return null;

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100dvh",
    backgroundColor: `rgba(0,0,0,${overlayOpacity})`,
    zIndex: zIndex - 1,
  };

  const sidebarStyle = {
    position: "fixed",
    top: 0,
    right: 0,
    width,
    maxWidth: "100vw",
    height: "100dvh",
    boxSizing: "border-box",
    backgroundColor: "var(--surface-color, #fff)",
    boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
    zIndex,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const headerStyle = {
    padding: "1rem",
    borderBottom: "1px solid var(--border-color, #e0e0e0)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "var(--surface-color, #fff)",
    flexShrink: 0,
  };

  const titleStyle = {
    margin: 0,
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "var(--text-primary, #333)",
  };

  const closeButtonStyle = {
    background: "none",
    border: "none",
    fontSize: "1.5rem",
    cursor: "pointer",
    padding: "0.25rem",
    lineHeight: 1,
    color: "var(--text-secondary, #666)",
  };

  const contentStyle = {
    flex: 1,
    overflow: "auto",
    padding: contentPadding,
  };

  return (
    <>
      {showOverlay && (
        <div
          onClick={onClose}
          style={overlayStyle}
          data-testid={dataTestId ? `${dataTestId}-overlay` : undefined}
        />
      )}
      <div
        className={`sidebar-shell safe-area-sidebar ${className}`}
        style={sidebarStyle}
        data-testid={dataTestId}
        role="complementary"
        aria-label={title}
      >
        <div style={headerStyle}>
          <h3 style={titleStyle}>{title}</h3>
          {showCloseButton && (
            <button
              onClick={onClose}
              style={closeButtonStyle}
              aria-label="Close sidebar"
              data-testid={dataTestId ? `${dataTestId}-close` : undefined}
            >
              ×
            </button>
          )}
        </div>
        <div style={{ ...contentStyle, position: 'relative' }}>
          {children}
        </div>
      </div>
    </>
  );
}

export default SidebarShell;
